import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ArrowRightLeft, Search, Check, CheckCheck, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useItemTransfer } from "@/hooks/useItemTransfer";
import { toast } from "sonner";

interface SentItem {
  id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  notes?: string | null;
  product_id?: string | null;
}

interface DestinationInfo {
  type: "table" | "command";
  id: string;
  number: string;
  orderId: string | null;
}

interface TransferItemsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sentItems: SentItem[];
  currentOrderId: string | null;
  currentType: "table" | "command";
  currentNumber: string;
  onSuccess: () => void;
}

export function TransferItemsDialog({
  open,
  onOpenChange,
  sentItems,
  currentOrderId,
  currentType,
  currentNumber,
  onSuccess,
}: TransferItemsDialogProps) {
  const { activeRestaurant } = useRestaurantContext();
  const { transferItem } = useItemTransfer();

  const [destType, setDestType] = useState<"table" | "command">(currentType);
  const [destNumber, setDestNumber] = useState("");
  const [destination, setDestination] = useState<DestinationInfo | null>(null);
  const [searching, setSearching] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [transferring, setTransferring] = useState(false);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

  const selectedTotal = useMemo(() => {
    return sentItems
      .filter((i) => selectedItems.has(i.id))
      .reduce((sum, i) => sum + Number(i.total_price), 0);
  }, [sentItems, selectedItems]);

  const handleSearch = async () => {
    if (!activeRestaurant || !destNumber.trim()) return;
    if (destType === currentType && destNumber.trim() === currentNumber) {
      toast.error("Não é possível transferir para a mesma origem");
      return;
    }

    setSearching(true);
    try {
      if (destType === "table") {
        const { data: table } = await supabase
          .from("tables")
          .select("id, number, current_order_id, grouped_to_order_id")
          .eq("restaurant_id", activeRestaurant.id)
          .eq("number", destNumber.trim())
          .maybeSingle();

        if (!table) {
          toast.error(`Mesa ${destNumber} não encontrada`);
          return;
        }
        if (table.grouped_to_order_id) {
          toast.error("Esta mesa está agrupada a outra conta");
          return;
        }
        setDestination({
          type: "table",
          id: table.id,
          number: table.number,
          orderId: table.current_order_id,
        });
      } else {
        const { data: command } = await supabase
          .from("commands")
          .select("id, number, current_order_id, grouped_to_order_id")
          .eq("restaurant_id", activeRestaurant.id)
          .eq("number", destNumber.trim())
          .maybeSingle();

        if (!command) {
          toast.error(`Comanda ${destNumber} não encontrada`);
          return;
        }
        if (command.grouped_to_order_id) {
          toast.error("Esta comanda está agrupada a outra conta");
          return;
        }
        setDestination({
          type: "command",
          id: command.id,
          number: command.number,
          orderId: command.current_order_id,
        });
      }
    } catch {
      toast.error("Erro ao buscar destino");
    } finally {
      setSearching(false);
    }
  };

  const toggleItem = (id: string) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedItems.size === sentItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(sentItems.map((i) => i.id)));
    }
  };

  const getOrCreateDestinationOrder = async (): Promise<string | null> => {
    if (!activeRestaurant || !destination) return null;

    if (destination.orderId) return destination.orderId;

    // Create a new order for the destination
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: newOrder, error } = await supabase
      .from("orders")
      .insert({
        restaurant_id: activeRestaurant.id,
        status: "pending" as const,
        subtotal: 0,
        total: 0,
        created_by: user.id,
        table_id: destination.type === "table" ? destination.id : undefined,
        command_id: destination.type === "command" ? destination.id : undefined,
      })
      .select("id")
      .single();

    if (error || !newOrder) return null;

    // Update the table/command to point to this order
    if (destination.type === "table") {
      await supabase
        .from("tables")
        .update({ current_order_id: newOrder.id, status: "occupied" })
        .eq("id", destination.id);
    } else {
      await supabase
        .from("commands")
        .update({ current_order_id: newOrder.id, status: "occupied" })
        .eq("id", destination.id);
    }

    setDestination((prev) => prev ? { ...prev, orderId: newOrder.id } : null);
    return newOrder.id;
  };

  const handleTransfer = async () => {
    if (!currentOrderId || !destination || selectedItems.size === 0) return;

    setTransferring(true);
    try {
      const toOrderId = await getOrCreateDestinationOrder();
      if (!toOrderId) {
        toast.error("Erro ao preparar pedido de destino");
        return;
      }

      const itemsToTransfer = sentItems.filter((i) => selectedItems.has(i.id));

      for (const item of itemsToTransfer) {
        const success = await transferItem({
          orderItemId: item.id,
          fromOrderId: currentOrderId,
          toOrderId,
          quantity: item.quantity,
        });
        if (!success) {
          toast.error(`Erro ao transferir "${item.product_name}"`);
          return;
        }
      }

      toast.success(
        `${itemsToTransfer.length} ${itemsToTransfer.length === 1 ? "item transferido" : "itens transferidos"} para ${destType === "table" ? "Mesa" : "Comanda"} ${destination.number}`
      );
      onSuccess();
      handleClose();
    } catch {
      toast.error("Erro ao transferir itens");
    } finally {
      setTransferring(false);
    }
  };

  const handleTransferAll = async () => {
    setSelectedItems(new Set(sentItems.map((i) => i.id)));
    // We need to wait for state update, so just select all - user clicks confirm
  };

  const handleClose = () => {
    setDestNumber("");
    setDestination(null);
    setSelectedItems(new Set());
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#0b1020] border-[#1e2d52] text-[#c8dff5] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#c8dff5]">
            <ArrowRightLeft className="h-5 w-5 text-[#00aaff]" />
            Transferir Itens
          </DialogTitle>
        </DialogHeader>

        {/* Destination selector */}
        <div className="space-y-3">
          <label className="text-xs text-[#4a7abf] font-medium">Transferir para:</label>

          <ToggleGroup
            type="single"
            value={destType}
            onValueChange={(v) => {
              if (v) {
                setDestType(v as "table" | "command");
                setDestination(null);
              }
            }}
            className="justify-start"
          >
            <ToggleGroupItem
              value="table"
              className="data-[state=on]:bg-[#0a3a6e] data-[state=on]:text-[#4dd0ff] border-[#1e2d52] text-[#7aabdd]"
            >
              Mesa
            </ToggleGroupItem>
            <ToggleGroupItem
              value="command"
              className="data-[state=on]:bg-[#0a3a6e] data-[state=on]:text-[#4dd0ff] border-[#1e2d52] text-[#7aabdd]"
            >
              Comanda
            </ToggleGroupItem>
          </ToggleGroup>

          <div className="flex gap-2">
            <Input
              type="text"
              inputMode="numeric"
              placeholder={`Nº da ${destType === "table" ? "mesa" : "comanda"}`}
              value={destNumber}
              onChange={(e) => {
                setDestNumber(e.target.value);
                setDestination(null);
              }}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="bg-[#111829] border-[#1e2d52] text-[#c8dff5] placeholder:text-[#4a7abf]/50"
            />
            <Button
              onClick={handleSearch}
              disabled={!destNumber.trim() || searching}
              className="bg-[#0a3a6e] border border-[#0077cc] text-[#4dd0ff] hover:bg-[#0077cc]/50 shrink-0"
            >
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Buscar
            </Button>
          </div>

          {destination && (
            <div className="bg-[#111829] border border-[#1e2d52] rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[#00e676]">
                  ✓ {destType === "table" ? "Mesa" : "Comanda"} {destination.number}
                </span>
                <span className="text-xs text-[#4a7abf]">
                  {destination.orderId ? "Pedido ativo" : "Sem pedido (será criado)"}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Items list */}
        {destination && (
          <div className="space-y-3 mt-2">
            <div className="flex items-center justify-between">
              <label className="text-xs text-[#4a7abf] font-medium">
                Selecione os itens ({selectedItems.size}/{sentItems.length}):
              </label>
              <Button
                variant="ghost"
                size="sm"
                onClick={selectAll}
                className="text-[#00aaff] hover:text-[#4dd0ff] hover:bg-[#0a3a6e]/50 text-xs gap-1"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                {selectedItems.size === sentItems.length ? "Desmarcar" : "Selecionar"} Todos
              </Button>
            </div>

            <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
              {sentItems.map((item) => {
                const isSelected = selectedItems.has(item.id);
                return (
                  <button
                    key={item.id}
                    onClick={() => toggleItem(item.id)}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-lg border transition-colors text-left ${
                      isSelected
                        ? "bg-[#0a3a6e]/60 border-[#0077cc] text-[#c8dff5]"
                        : "bg-[#111829] border-[#1e2d52] text-[#7aabdd] hover:border-[#0077cc]/50"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                        isSelected
                          ? "bg-[#0077cc] border-[#0077cc]"
                          : "border-[#1e2d52]"
                      }`}
                    >
                      {isSelected && <Check className="h-3.5 w-3.5 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{item.product_name}</div>
                      {item.notes && (
                        <div className="text-[10px] text-[#4a7abf] truncate">{item.notes}</div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-[#4a7abf]">×{item.quantity}</div>
                      <div className="text-sm font-medium">{formatCurrency(Number(item.total_price))}</div>
                    </div>
                  </button>
                );
              })}
            </div>

            {selectedItems.size > 0 && (
              <div className="bg-[#0a3d1e] border border-[#00c853]/30 rounded-lg p-2.5 flex items-center justify-between">
                <span className="text-xs text-[#4a7abf]">Total selecionado:</span>
                <span className="text-base font-bold text-[#00e676]">{formatCurrency(selectedTotal)}</span>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="flex-1 bg-[#111829] border-[#1e2d52] text-[#7aabdd] hover:bg-[#1e2d52]"
          >
            Cancelar
          </Button>
          {destination && (
            <>
              <Button
                onClick={handleTransferAll}
                disabled={transferring || sentItems.length === 0}
                className="flex-1 bg-[#0a3a6e] border border-[#0077cc] text-[#4dd0ff] hover:bg-[#0077cc]/50"
              >
                <CheckCheck className="h-4 w-4" />
                Transferir Tudo
              </Button>
              <Button
                onClick={handleTransfer}
                disabled={transferring || selectedItems.size === 0}
                className="flex-1 bg-[#0a3d1e] border border-[#00c853] text-[#00e676] hover:bg-[#00c853]/30"
              >
                {transferring ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRightLeft className="h-4 w-4" />
                )}
                Confirmar ({selectedItems.size})
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
